'use client';

/**
 * Light / dark theme.
 *
 * Modelled on LiteMode, and for the same reason: the preference is a class on
 * <html>, applied by an inline script before React hydrates. Keeping it in
 * React state instead would repaint the whole app one frame after load, which
 * on a light-theme preference means a full-screen flash of near-black.
 *
 * The class is the single source of truth. `useTheme` mirrors it into React
 * only so components that need to *branch* on it (React Flow's `colorMode`,
 * the toast surface) can re-render; the styling itself is all CSS variables in
 * tailwind.css.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Toaster } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { useOverlayRoot } from '@/lib/overlayRoot';

export const THEME_KEY = 'archlyy.theme';
export type ThemeName = 'dark' | 'light';

/** The class that tailwind.css keys the light palette off. */
const LIGHT_CLASS = 'theme-light';

/**
 * Runs before hydration. Falls back to the OS preference when the user has
 * never chosen, so someone on a light desktop does not get a dark app by
 * default -- but an explicit choice always wins.
 */
export function ThemeScript() {
  const script = `try{var t=localStorage.getItem('${THEME_KEY}');if(!t){t=window.matchMedia&&window.matchMedia('(prefers-color-scheme: light)').matches?'light':'dark'}if(t==='light'){document.documentElement.classList.add('${LIGHT_CLASS}')}}catch(e){}`;
  return <script dangerouslySetInnerHTML={{ __html: script }} />;
}

/* A plain subscriber set rather than context: the theme is read in a handful
   of unrelated places (top nav, canvas, toaster) that have no common provider
   below the root layout, and it changes about once a session. */
const listeners = new Set<(t: ThemeName) => void>();

function readTheme(): ThemeName {
  if (typeof document === 'undefined') return 'dark';
  return document.documentElement.classList.contains(LIGHT_CLASS) ? 'light' : 'dark';
}

export function setTheme(next: ThemeName) {
  document.documentElement.classList.toggle(LIGHT_CLASS, next === 'light');
  try {
    localStorage.setItem(THEME_KEY, next);
  } catch {
    // Private mode or a full quota: the choice still holds for this session.
  }
  listeners.forEach((fn) => fn(next));
}

/**
 * Current theme, plus a setter.
 *
 * Starts as 'dark' on the server and on the first client render so markup
 * matches; the effect corrects it immediately after mount. Anything that
 * would flash from getting this wrong for one frame is handled by the CSS
 * variables, not by this hook.
 */
export function useTheme(): [ThemeName, (t: ThemeName) => void] {
  const [theme, setLocal] = useState<ThemeName>('dark');

  useEffect(() => {
    setLocal(readTheme());
    listeners.add(setLocal);
    return () => {
      listeners.delete(setLocal);
    };
  }, []);

  return [theme, setTheme];
}

/**
 * The app's toast surface, following the theme.
 *
 * Lives here rather than in layout.tsx because sonner needs `theme` as a prop
 * -- it draws its own close button and icons from it -- and layout.tsx is a
 * server component that cannot subscribe to anything.
 */
export function ThemedToaster() {
  const [theme] = useTheme();
  const overlayRoot = useOverlayRoot();

  const toaster = (
    <Toaster
      position="bottom-right"
      theme={theme}
      toastOptions={{
        style: {
          background: 'var(--card)',
          border: '1px solid var(--border)',
          color: 'var(--foreground)',
        },
      }}
    />
  );

  /* Declared at the root of the layout, which is outside the element the
     canvas puts into fullscreen -- and the fullscreen top layer paints over
     everything that is not inside it. A toast is how a failed edit reports
     itself, so it has to move into that subtree while fullscreen is on.
     Body is the no-fullscreen case and renders in place, which keeps the
     server-rendered markup and the hydrated markup identical. */
  if (!overlayRoot || overlayRoot === document.body) return toaster;
  return createPortal(toaster, overlayRoot);
}

/**
 * Top-nav pill. One control, showing the theme you would switch *to* -- so in
 * the light theme it reads "Dark" and in the dark theme it reads "Light", and
 * clicking it flips. There is deliberately no second button and no third
 * "auto" state: the *initial* value already follows the OS preference (see
 * ThemeScript), and an explicit choice is meant to stick after that.
 *
 * The title spells the action out, because a label naming the other theme is
 * ambiguous on its own -- "Dark" could equally mean "you are in dark mode".
 */
export function ThemeToggle() {
  const [theme, apply] = useTheme();
  const light = theme === 'light';

  const toggle = useCallback(() => apply(light ? 'dark' : 'light'), [light, apply]);

  return (
    <button
      onClick={toggle}
      aria-pressed={light}
      className="nav-pill flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all duration-150 flex-shrink-0"
      /* Indigo when it offers dark, amber when it offers light -- the accent
         previews the theme you would get, matching the glyph beside it. */
      style={{ ['--tile-accent' as string]: light ? '#818cf8' : '#f59e0b' }}
      title={
        light
          ? 'Light theme is on — click to switch to dark'
          : 'Dark theme is on — click to switch to light'
      }
    >
      <Icon name={light ? 'MoonIcon' : 'SunIcon'} size={12} />
      {light ? 'Dark' : 'Light'}
    </button>
  );
}
